"use client";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { searchProfile } from "@/lib/account/profile";
import type { EnrichedProfile } from "@/lib/supabase/types";
import { cn } from "@/lib/utils";
import * as Mention from "@diceui/mention";
import { useEffect, useState } from "react";

interface MentionBoxProps
	extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
	className?: string;
	value?: string;
	onValueChange?: (value: string) => void;
}

export function MentionBox({
	className,
	value,
	onValueChange,
	...rest
}: MentionBoxProps) {
	const [users, setUsers] = useState<EnrichedProfile[] | null>(null);
	const [query, setQuery] = useState<string>("");

	useEffect(() => {
		const fetchUsers = async () => {
			const lastWord = query.split(" ").pop();
			if (lastWord?.startsWith("@")) {
				const searchTerm = lastWord?.substring(1);
				console.debug("searching for profile with username: ", searchTerm);
				const { data, error } = await searchProfile(searchTerm || "");
				if (error) {
					console.error(error);
				} else {
					setUsers(data);
				}
			}
		};
		fetchUsers();
	}, [query]);

	return (
		<Mention.Root
			className={cn(
				"w-full **:data-tag:rounded **:data-tag:bg-blue-200 **:data-tag:py-px **:data-tag:text-blue-950 dark:**:data-tag:bg-blue-800 dark:**:data-tag:text-blue-50",
				className,
			)}
			onInputValueChange={(value) => {
				setQuery(value);
				onValueChange?.(value);
			}}
		>
			<Mention.Input
				placeholder="Type @ to mention someone..."
				className="flex min-h-[60px] w-full rounded-md border border-zinc-200 bg-transparent px-3 py-2 text-base shadow-xs placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-zinc-800 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:border-zinc-800 dark:focus-visible:ring-zinc-300"
				asChild
			>
				<textarea value={value || ""} />
			</Mention.Input>
			<Mention.Portal>
				<Mention.Content className="data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 min-w-[var(--dice-anchor-width)] overflow-hidden rounded-md border border-zinc-200 bg-white p-1 text-zinc-950 shadow-md data-[state=closed]:animate-out data-[state=open]:animate-in dark:border-zinc-800 dark:bg-zinc-950 dark:text-zinc-50">
					{users?.map((user) => (
						<Mention.Item
							key={user.id}
							value={user.username || ""}
							className="relative flex flex-row gap-2 w-full cursor-default select-none rounded-sm px-2 py-1.5 text-sm outline-hidden data-disabled:pointer-events-none data-highlighted:bg-zinc-100 data-highlighted:text-zinc-900 data-disabled:opacity-50 dark:data-highlighted:bg-zinc-800 dark:data-highlighted:text-zinc-50"
						>
							<Avatar>
								<AvatarImage src={user.avatar_url || ""} />
								<AvatarFallback>{user.name?.[0] || ""}</AvatarFallback>
							</Avatar>
							<div className="flex flex-col">
								<span className="text-sm">{user.name}</span>
								<span className="text-muted-foreground text-xs">
									{user.username}
								</span>
							</div>
						</Mention.Item>
					))}
				</Mention.Content>
			</Mention.Portal>
		</Mention.Root>
	);
}
